import { token } from '@atlaskit/tokens';
import { useFlaggingCallback } from '@databases/banners';
import ThreadPopup from '@databases/comments/ThreadPopup';
import useCommentContext from '@databases/comments/hooks/useCommentContext';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import ValidationStatus, {
  ValidationStatusContainer,
} from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { Adjacents } from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { useSkeletonEnabled } from '@databases/skeleton';
import { DatabaseFieldType, DatabaseValue } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import {
  selectUnresolvedThreadsByValueIdOptional,
  selectValueIdFromReactionsIdOptional,
} from '@databases/utils/redux/comments/selectors';
import { getValueId } from '@databases/utils/utils';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { PresenceInfo, usePresence } from '../../presence';
import { InlineEditContainer, InlineEditStateless } from '../InlineEdit';
import InlineEditWrapper from '../InlineEditWrapper';
import { EditMode, FIELD_TYPES } from '../field-types/fieldTypes';

import DatabaseValueEdit from './DatabaseValueEdit';
import DatabaseValueView from './DatabaseValueView';
import { DatabaseValueViewContainer } from './utils';

export const StyledInlineEditWrapper = styled(InlineEditWrapper)<{
  isSelected?: boolean;
  isFirstInRow?: boolean;
  selectedAdjacentCells?: Adjacents;
  presenceInfo?: PresenceInfo;
  hasThreads?: boolean;
}>`
  height: 100%;

  > ${ValidationStatusContainer},
    > ${ValidationStatusContainer}
    > ${InlineEditContainer} {
    flex-grow: 1;

    *:not(a):not(span):not(#comment-threads-popup):not(#comment-threads-popup *) {
      // Among other things, this hides the border of input fields because we
      // want to use the border of the BorderContainer. We want to use the smart
      // card-provided border for smart cards, so we exclude <a> and <span>. In
      // NCTD-2440, we want to look into improving this styling.
      // Also excludes comments related elements.
      border-color: transparent;
    }
  }

  &:hover {
    > ${ValidationStatusContainer} > ${InlineEditContainer} {
      background: ${({ isSelected, hasThreads }) =>
        isSelected || hasThreads ? 'initial' : token('elevation.surface.hovered')};
    }
  }

  > ${DatabaseValueViewContainer},
    > div
    > ${DatabaseValueViewContainer},
    > ${ValidationStatusContainer}
    > ${InlineEditContainer}
    div
    > ${DatabaseValueViewContainer} {
    display: inline-block;
    padding: ${({ isFirstInRow }) => (isFirstInRow ? '10px 8px 10px 20px' : '10px 8px')};
    max-width: 100%;
    box-sizing: border-box;
  }

  min-height: 41px !important;

  && textarea {
    padding: ${({ isFirstInRow }) => (isFirstInRow ? '8px 6px 8px 18px' : '8px 6px')};
  }

  && input {
    padding: ${({ isFirstInRow }) => isFirstInRow && '8px 6px 8px 18px'};
  }

  ${({ presenceInfo }) =>
    presenceInfo &&
    `z-index: 2;

  &::before {
    content: attr(data-presence-name);
    max-width: 65%;
    color: white;
    position: absolute;
    bottom: 100%;
    right: 0;
    height: fit-content;
    background: ${presenceInfo.color};
    pointer-events: none;
    text-align: right;
    padding: ${token('space.025')} ${token('space.050')} ${token('space.025')} ${token(
      'space.050'
    )};
    border-radius: 4px 4px 0 0;
  }

  `}
`;

export interface DatabaseValueInlineEditStatelessProps {
  contentId: string;
  fieldId: string;
  entryId: string;
  isEditing: boolean;
  onIsEditingChange: (isEditing: boolean) => void;
  isReadonly?: boolean;
  isContentEditable?: boolean;
  isSelected?: boolean;
  markAsDeleted?: boolean;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  isFirstInRow?: boolean;
  selectedAdjacentCells?: Adjacents;
  enableSelection?: boolean;
  panel?: boolean;
  isHoverCell?: boolean;
  setIsHoverCell?: (isHover: boolean) => void;
}

/**
 * Renders an inline edit field to edit a database value. Automatically picks the right view/edit components based
 * on the field type of the value. When saving the changes, a database update is automatically drafted.
 */
export const DatabaseValueInlineEditStateless: FunctionComponent<
  DatabaseValueInlineEditStatelessProps
> = ({
  contentId,
  fieldId,
  entryId,
  isEditing,
  onIsEditingChange,
  isReadonly,
  isContentEditable,
  isSelected,
  markAsDeleted,
  onIsSelectedChange,
  isFirstInRow,
  selectedAdjacentCells,
  enableSelection,
  panel = false,
  isHoverCell = false,
  setIsHoverCell,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const isInlineCommentsEnabled = useBooleanFeatureFlag(
    'databases-frontend-inline-comments'
  );
  const [isThreadPopupHovered, setIsThreadPopupHovered] = useState(false);
  const {
    openThread,
    setOpenThread,
    focusedCommentContentId,
    setFocusedCommentContentId,
  } = useCommentContext();
  const scope = useScope();
  const { formatMessage } = useIntl();
  const [value, editMode, field, threads, totalCommentCount, focusedThreadValueId] =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        scope,
        includeDeleted: true,
      });
      const entry = getDatabaseEntryRequired(databaseDraft, entryId);
      const field = getDatabaseFieldRequired(databaseDraft, fieldId);
      const value = getDatabaseValue(databaseDraft, field, entry);

      const editMode = FIELD_TYPES[field.type].editMode(
        { database: databaseDraft, field, entry, value },
        state
      );

      const threads = selectUnresolvedThreadsByValueIdOptional(state, {
        contentId,
        fieldId,
        entryId,
      });

      const totalCommentCount =
        threads?.reduce((acc, thread) => {
          return acc + thread.commentCount;
        }, 0) ?? 0;

      const focusedThreadValueId = selectValueIdFromReactionsIdOptional(state, {
        contentId,
        reactionsId: focusedCommentContentId,
      });

      return [
        value,
        editMode,
        field,
        threads,
        totalCommentCount,
        focusedThreadValueId,
      ] as const;
    });

  const handleReset = useFlaggingCallback(
    (value: DatabaseValue) => {
      dispatch(
        addChangeToDraft(contentId, scope, {
          type: DatabaseChangeType.SET_VALUE,
          fieldId,
          entryId,
          value,
          change: null, // ignore reset in collab mode, since we don't know what delta to pass?
        })
      );
    },
    [contentId, dispatch, entryId, fieldId, scope]
  );

  const fieldSettingsRequired =
    (field.type === DatabaseFieldType.JIRA_ISSUE &&
      !field.settings.jiraServerId &&
      !field.settings.cloudId) ||
    (field.type === DatabaseFieldType.ENTRY_LINK &&
      !field.settings.databaseId &&
      !field.settings.contentId);

  const canEdit = !isReadonly && !fieldSettingsRequired;

  const isSkeletonEnabled = useSkeletonEnabled();
  const enableEditMode =
    !canEdit || editMode === EditMode.READ_ONLY || isSkeletonEnabled
      ? false
      : (isEditing && editMode === EditMode.INLINE_EDIT) || editMode === EditMode.EDIT;

  const presenceInfo = usePresence(contentId, entryId, fieldId, isSelected || isEditing);

  const isAttachmentField = useMemo(
    () => field.type === DatabaseFieldType.ATTACHMENT,
    [field.type]
  );

  const valueId = useMemo(() => {
    return getValueId(entryId, fieldId);
  }, [entryId, fieldId]);

  const toggleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenThread({
      valueId: openThread.valueId === valueId ? undefined : valueId,
      panel: panel,
    });
    // If manually toggled, we should clear the focused thread since it's derived from URL
    setFocusedCommentContentId(undefined);
  };

  const hasThreads = useMemo(() => threads && threads.length > 0, [threads]);
  const shouldShowHoverButton = useMemo(
    () =>
      isInlineCommentsEnabled &&
      !isEditing &&
      (isHoverCell || openThread.valueId === valueId),
    [isEditing, isHoverCell, isInlineCommentsEnabled, openThread.valueId, valueId]
  );

  useEffect(() => {
    if (!isInlineCommentsEnabled) {
      return;
    }

    if (focusedThreadValueId === openThread.valueId) {
      const targetCell = document.getElementById('focused-cell');
      targetCell?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
        inline: 'nearest',
      });
    }
  }, [
    openThread,
    fieldId,
    entryId,
    isInlineCommentsEnabled,
    focusedThreadValueId,
    valueId,
    dispatch,
  ]);

  return (
    <StyledInlineEditWrapper
      markAsDeleted={markAsDeleted}
      hideBorderOnFocus
      isSelected={isSelected}
      isFirstInRow={isFirstInRow}
      selectedAdjacentCells={selectedAdjacentCells}
      presenceInfo={presenceInfo}
      hasThreads={hasThreads}
    >
      <ValidationStatus error={undefined}>
        {() => (
          <InlineEditStateless<DatabaseValue>
            isSelected={isSelected}
            readView={() => (
              <>
                <DatabaseValueView
                  contentId={contentId}
                  entryId={entryId}
                  fieldId={fieldId}
                  // Prevent focussing anything inside the value (such as a link) by using Tab, as we want the Tab key
                  // to navigate between the inline-edit fields.
                  noFocus
                  isReadonly={isContentEditable === false}
                />
                {isInlineCommentsEnabled && !isAttachmentField && (
                  <ThreadPopup
                    contentId={contentId}
                    entryId={entryId}
                    fieldId={fieldId}
                    valueId={valueId}
                    shouldShowHoverButton={shouldShowHoverButton}
                    threads={threads}
                    totalCommentCount={totalCommentCount}
                    hasThreads={!!threads && threads.length > 0}
                    openThread={openThread}
                    toggleComment={toggleComment}
                    setIsThreadPopupHovered={setIsThreadPopupHovered}
                    placement={panel ? 'bottom-end' : 'right-start'}
                    panel={panel}
                    setIsHoverCell={setIsHoverCell}
                  />
                )}
              </>
            )}
            editView={() => (
              <>
                <DatabaseValueEdit
                  contentId={contentId}
                  entryId={entryId}
                  fieldId={fieldId}
                  noValidation={editMode === EditMode.INLINE_EDIT}
                  isSeamless={editMode === EditMode.EDIT}
                  autoFocus={isEditing}
                />
                {/* Attachment field always renders editView */}
                {isInlineCommentsEnabled && !isEditing && isAttachmentField && (
                  <ThreadPopup
                    contentId={contentId}
                    entryId={entryId}
                    fieldId={fieldId}
                    valueId={valueId}
                    shouldShowHoverButton={
                      isAttachmentField ? shouldShowHoverButton : false
                    }
                    threads={threads}
                    totalCommentCount={totalCommentCount}
                    hasThreads={!!threads && threads.length > 0}
                    openThread={openThread}
                    toggleComment={toggleComment}
                    setIsThreadPopupHovered={setIsThreadPopupHovered}
                    placement={panel ? 'left-start' : 'right-start'}
                    panel={panel}
                  />
                )}
              </>
            )}
            isEditing={enableEditMode}
            onIsEditingChange={onIsEditingChange}
            value={value}
            onReset={handleReset}
            onIsSelectedChange={onIsSelectedChange}
            enableSelection={isThreadPopupHovered ? false : enableSelection}
            field={field}
            entryId={entryId}
            tooltipContent={
              fieldSettingsRequired
                ? formatMessage(
                    messages['value-inline-edit.required-field-missing-tooltip']
                  )
                : null
            }
            hasThreads={!isEditing && hasThreads}
            shouldShowHoverButton={shouldShowHoverButton}
          />
        )}
      </ValidationStatus>
    </StyledInlineEditWrapper>
  );
};

export interface DatabaseValueInlineEditProps
  extends Omit<DatabaseValueInlineEditStatelessProps, 'isEditing' | 'onIsEditingChange'> {
  /** If true, the field will be in edit mode the first time it is rendered. */
  initialIsEditing?: boolean;
}

const DatabaseValueInlineEdit: FunctionComponent<DatabaseValueInlineEditProps> = ({
  initialIsEditing = false,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(initialIsEditing);

  return (
    <DatabaseValueInlineEditStateless
      isEditing={isEditing}
      onIsEditingChange={setIsEditing}
      {...props}
    />
  );
};

export default React.memo(DatabaseValueInlineEdit);
